// The whole A4 landscape sheet, as one component tree rendered in two modes.
//
// @aiPurpose Guarantee that what a manager edits and what a customer receives
//   are the same markup: `editing` only decides whether a cell is a control or
//   a text node, never which bands, rows or figures the document has. The one
//   blank header cell over the pinned column (DocumentTable) is the exception.
// @aiInvariant View mode renders no form element at all. Every edit-only
//   affordance is either absent from the tree or carries `data-editing`, which
//   the print stylesheet removes.
import type { ReactNode } from 'react';
import type { LogisticsCalculationPublicDto } from '@magimp/shared';
import { DocumentEditingProvider } from './DocumentCell.js';
import { DocumentMasthead } from './DocumentMasthead.js';
import { DocumentPayable } from './DocumentPayable.js';
import { DocumentTable } from './DocumentTable.js';
// Import order is the cascade order: the document, then the manager's editing
// layer over it, then what a printer does with the result.
import './logistics-calculation-document.css';
import './logistics-calculation-document-edit.css';
import './logistics-calculation-document-print.css';

export interface DocumentSheetProps {
  dto: LogisticsCalculationPublicDto | null;
  publicCode: string;
  editing?: boolean;
  /**
   * Stable react-hook-form field ids for the cargo array, edit mode only. The
   * array itself is owned by the surface that owns the form, so the document
   * never opens a second `useFieldArray` on the same name.
   */
  cargoKeys?: readonly string[];
  onRemoveCargo?: (index: number) => void;
  cargoPicker?: ReactNode;
}

export function DocumentSheet({
  dto,
  publicCode,
  editing = false,
  cargoKeys,
  onRemoveCargo,
  cargoPicker,
}: DocumentSheetProps) {
  const cargoCount = editing ? (cargoKeys?.length ?? 0) : (dto?.cargos.length ?? 0);
  return (
    <DocumentEditingProvider editing={editing}>
      <article
        className="doc-sheet"
        data-testid="logistics-calculation-document"
        data-editing={editing ? '' : undefined}
      >
        <DocumentMasthead dto={dto} publicCode={publicCode} />
        <div className="doc-rule" />
        <DocumentTable
          dto={dto}
          cargoCount={cargoCount}
          cargoKeys={cargoKeys}
          onRemoveCargo={onRemoveCargo}
        />
        {cargoPicker}
        <DocumentPayable dto={dto} />
      </article>
    </DocumentEditingProvider>
  );
}
